import { FC } from 'react';
import { Box, Container, Flex, Text } from 'roo-ui/components';
import Icon from 'components/Icon';
import { HiddenOnSmallScreen, ShowOnSmallScreen } from 'components/Layout';
import { BackLink, CloseButton, PageTitle, UppercaseText } from './primitives';
import { useDispatch, useSelector } from 'react-redux';
import { getSsoUserMembershipTier } from 'store/authentication/sso/selectors';
import { getAuthMethod, getIsAuthenticatedWithGatekeeper } from 'store/authentication/selectors';
import { removeSBAAuthentication } from 'store/authentication/sba/actions';
import { AuthMethod } from 'config/constants';

interface PageHeaderProps extends Omit<InnerPageHeaderProps, 'tier' | 'onClickCloseSession'> {}

const PageHeader: FC<PageHeaderProps> = (props) => {
  const tier = useSelector(getSsoUserMembershipTier);
  const authMethod = useSelector(getAuthMethod);
  const isAuthenticatedWithGatekeeper = useSelector(getIsAuthenticatedWithGatekeeper);
  const dispatch = useDispatch();

  const onClickCloseSession =
    authMethod === AuthMethod.SBA && isAuthenticatedWithGatekeeper ? () => dispatch(removeSBAAuthentication()) : undefined;

  return <InnerPageHeader tier={tier} onClickCloseSession={onClickCloseSession} {...props} />;
};

interface InnerPageHeaderProps {
  title: string;
  tier: string;
  bookingRef?: string;
  backLinkUrl?: string;
  backLinkTitle?: string;
  onClickBackLink?: Function;
  onClickCloseSession?: () => any;
}

export const InnerPageHeader: FC<InnerPageHeaderProps> = ({
  title,
  tier,
  bookingRef,
  backLinkUrl,
  backLinkTitle,
  onClickBackLink,
  onClickCloseSession,
}) => (
  <Flex flexDirection="column" data-testid="page-header" borderTop={[1, 1, 0]} borderColor={['greys.alto', 'greys.alto', 'greys.alto']}>
    <Flex minHeight={['60px', '150px']} bg="brand.primary" alignItems="center">
      <Container>
        <Flex justifyContent="space-between" alignItems={['baseline', 'normal']}>
          <Box>
            {backLinkTitle && (
              <BackLink
                mb={[0, 3]}
                fontSize="lg"
                to={backLinkUrl}
                title={backLinkTitle}
                onClick={onClickBackLink}
                data-testid="back-button"
              >
                <Icon name="keyboardBackspace" mr={2} size={22} /> {backLinkTitle}
              </BackLink>
            )}

            <PageTitle data-testid="page-title" hideOnMobile={!!backLinkTitle}>
              {title}
            </PageTitle>
          </Box>
          <Flex flexDirection="column" justifyContent="space-between" alignItems="flex-end">
            <Box>
              {onClickCloseSession && (
                <CloseButton onClick={onClickCloseSession} data-testid="close-session">
                  Close session <Icon name="close" ml={2} />
                </CloseButton>
              )}
            </Box>
            {bookingRef && (
              <HiddenOnSmallScreen>
                <Text color="white" textAlign="right" data-testid="desktop-booking-ref">
                  Booking reference: {bookingRef}
                </Text>
              </HiddenOnSmallScreen>
            )}
          </Flex>
        </Flex>
      </Container>
    </Flex>

    {bookingRef && (
      <ShowOnSmallScreen>
        <Flex bg="brand.tertiary">
          <Container>
            <Text color="white" data-testid="mobile-booking-ref">
              Booking reference: {bookingRef}
            </Text>
          </Container>
        </Flex>
      </ShowOnSmallScreen>
    )}

    <HiddenOnSmallScreen>
      <Flex bg="brand.tertiary">
        <Container>
          <Flex minHeight="36px" alignItems="center" justifyContent="flex-end">
            <UppercaseText color="white" fontSize="lg" letterSpacing="medium" data-testid="page-header-tier">
              {tier}
            </UppercaseText>
          </Flex>
        </Container>
      </Flex>
    </HiddenOnSmallScreen>
  </Flex>
);

export default PageHeader;
